import { type FetchResourceParams } from '#api/client'
import { RANGE_MINUTES, type RangeKey } from '#utils/timeRanges'

export type TracesFilters = {
  traceId: string
  carrier: string
  service: string
  status: '' | 'error' | 'success'
  filter: string
  range: RangeKey
  customFrom: string
  customTo: string
}

const DEFAULT_FILTERS: TracesFilters = {
  traceId: '',
  carrier: '',
  service: '',
  status: '',
  filter: '',
  range: '1h',
  customFrom: '',
  customTo: '',
}

const filterKeys = Object.keys(DEFAULT_FILTERS) as (keyof TracesFilters)[]

export const parseTracesFilters = (params: URLSearchParams): TracesFilters =>
  Object.fromEntries(
    filterKeys.map(key => [key, params.get(key) ?? DEFAULT_FILTERS[key]]),
  ) as TracesFilters

/** Only non-default values go in the URL */
export const toSearchParams = (filters: TracesFilters): URLSearchParams =>
  new URLSearchParams(
    filterKeys
      .filter(key => filters[key] !== DEFAULT_FILTERS[key])
      .map(key => [key, filters[key]]),
  )

/** Body filters scan request/response JSON, so they need a trace, carrier or layer */
export const isScoped = ({
  filter,
  traceId,
  carrier,
  service,
}: TracesFilters): boolean => !filter || !!(traceId || carrier || service)

const toDateRange = (
  { range, customFrom, customTo }: TracesFilters,
  now: Date,
): { startDate?: string; endDate?: string } => {
  if (range === 'custom') {
    return {
      startDate: customFrom && new Date(customFrom).toISOString(),
      endDate: customTo && new Date(customTo).toISOString(),
    }
  }

  if (range === 'all') {
    return {}
  }

  return {
    startDate: new Date(
      now.getTime() - RANGE_MINUTES[range] * 60_000,
    ).toISOString(),
    endDate: now.toISOString(),
  }
}

/** Query params for the traces endpoint; empty values are dropped by the fetch hook */
export const toTracesQuery = (
  filters: TracesFilters,
  now: Date = new Date(),
): FetchResourceParams => {
  const base = { limit: 100, includeBodies: 'false' }

  // Trace IDs are located through their headers, so the other filters would only hide it
  if (filters.traceId) {
    return { ...base, traceId: filters.traceId.trim() }
  }

  return {
    ...base,
    scac: filters.carrier.trim(),
    service: filters.service,
    status: filters.status,
    filter: filters.filter,
    ...toDateRange(filters, now),
  }
}
